import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

interface SidebarProps {
  collapsed: boolean;
  onClose?: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  section?: string;
  badge?: number;
  permissions?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onClose }) => {
  const { hasPermission, loading } = useAuth();

  const navItems: NavItem[] = [
    // Dashboard
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: "bi-speedometer2",
      section: "Main",
    },

    // User Management
    {
      path: "/users",
      label: "Users",
      icon: "bi-people",
      section: "Management",
      permissions: ["users:read"],
    },

    // Booking Management
    {
      path: "/bookings",
      label: "Bookings",
      icon: "bi-ticket-perforated",
      section: "Management",
      permissions: ["bookings:read"],
    },

    // Content Management
    {
      path: "/movies",
      label: "Movies",
      icon: "bi-film",
      section: "Content",
      permissions: ["movies:read"],
    },

    {
      path: "/cinemas",
      label: "Cinemas",
      icon: "bi-building",
      section: "Content",
      permissions: ["cinemas:read"],
    },

    // CMS
    {
      path: "/cms/pages",
      label: "Pages",
      icon: "bi-file-text",
      section: "CMS",
      permissions: ["cms:read"],
    },

    {
      path: "/cms/banners",
      label: "Banners",
      icon: "bi-image",
      section: "CMS",
      permissions: ["cms:read"],
    },

    {
      path: "/cms/mamangam-hall-booking",
      label: "Mamangam Hall Booking",
      icon: "bi-calendar-event",
      section: "CMS",
      permissions: ["cms:read"],
    },

    {
      path: "/cms/about-us",
      label: "About Us",
      icon: "bi-info-circle",
      section: "CMS",
      permissions: ["cms:read"],
    },

    {
      path: "/cms/advertise-with-us",
      label: "Advertise With Us",
      icon: "bi-megaphone",
      section: "CMS",
      permissions: ["cms:read"],
    },

    {
      path: "/cms/special-screening-bulk-booking",
      label: "Special Screening & Bulk Booking",
      icon: "bi-calendar2-event",
      section: "CMS",
      permissions: ["cms:read"],
    },

    // Media tab hidden
    // {
    //   path: '/cms/media',
    //   label: 'Media',
    //   icon: 'bi-folder',
    //   section: 'CMS',
    //   permissions: ['cms:read']
    // },

    // Notification Center (new)
    {
      path: "/notification-center",
      label: "Notifications",
      icon: "bi-bell",
      section: "Communication",
      permissions: ["notifications:read"],
    },
    // Brevo Sync
    // {
    //   path: "/brevo-sync",
    //   label: "Brevo Sync",
    //   icon: "bi-envelope-paper",
    //   section: "Communication",
    //   permissions: ["notifications:read"],
    // },
    // {
    //   path: "/notifications/templates",
    //   label: "Templates",
    //   icon: "bi-file-earmark-text",
    //   section: "Communication",
    //   permissions: ["notifications:read"],
    // },

    // Analytics
    {
      path: "/analytics",
      label: "Analytics",
      icon: "bi-graph-up",
      section: "Reports",
      permissions: ["analytics:read"],
    },
    {
      path: "/reports/bookings",
      label: "Bookings Report",
      icon: "bi-receipt",
      section: "Reports",
      permissions: ["analytics:read"],
    },
    {
      path: "/reports/cancellations",
      label: "Cancellations Report",
      icon: "bi-x-circle",
      section: "Reports",
      permissions: ["analytics:read"],
    },
    {
      path: "/reports/manual-refunds",
      label: "Manual Refunds",
      icon: "bi-cash-coin",
      section: "Reports",
      permissions: ["analytics:read"],
    },
    {
      path: "/reports/payout-summary",
      label: "Cinema Payout Summary",
      icon: "bi-bank",
      section: "Reports",
      permissions: ["analytics:read"],
    },
    {
      path: "/contact",
      label: "Feedback",
      icon: "bi-chat-dots",
      section: "Reports",
      permissions: ["analytics:read"],
    },

    // Settings
    {
      path: "/settings",
      label: "Settings",
      icon: "bi-gear",
      section: "System",
      permissions: ["settings:read"],
    },

    {
      path: "/settings/booking-fee-tiers",
      label: "Booking Fee Tiers",
      icon: "bi-cash-stack",
      section: "System",
      permissions: ["settings:read"],
    },

    // Admin Users
    {
      path: "/admin-users",
      label: "Admin Users",
      icon: "bi-shield-lock",
      section: "System",
      permissions: ["users:write"],
    },

    // Coupons
    {
      path: "/coupons",
      label: "Coupons",
      icon: "bi-ticket-perforated",
      section: "Marketing",
      permissions: ["coupons:read"],
    },

    // Beverages Management - Hidden
    // {
    //   path: '/beverages',
    //   label: 'Beverages',
    //   icon: 'bi-cup-straw',
    //   section: 'Content',
    //   permissions: ['cms:write']
    // },

    // Cities Management
    {
      path: "/cities",
      label: "Cities",
      icon: "bi-geo-alt",
    },

    // Offers Management - Hidden
    {
      path: "/offers",
      label: "Offers",
      icon: "bi-tag",
      section: "Marketing",
      permissions: ["offers:read"],
    },
  ];

  // Group items by section
  const groupedItems = navItems.reduce((acc, item) => {
    const canDisplay =
      !item.permissions ||
      loading ||
      item.permissions.some((permission) => hasPermission(permission));

    if (!canDisplay) {
      return acc;
    }

    const section = item.section || "Other";
    if (!acc.has(section)) {
      acc.set(section, []);
    }

    acc.get(section)!.push(item);
    return acc;
  }, new Map<string, NavItem[]>());

  return (
    <>
      <div
        className={`sidebar-backdrop ${collapsed ? "" : "show"}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div className={`sidebar ${collapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div>
            <h3>
              <i className="bi-camera-reels me-2"></i>
              MyCinemas
            </h3>
            <small className="text-muted">Admin Panel</small>
          </div>
          {onClose && (
            <button
              type="button"
              className="sidebar-close-btn d-md-none"
              onClick={onClose}
              aria-label="Close sidebar"
            >
              <i className="bi-x-lg"></i>
            </button>
          )}
        </div>

      <nav className="sidebar-nav">
        {Array.from(groupedItems.entries()).map(([section, items]) => (
          <div key={section} className="nav-section">
            <div className="nav-section-title">{section}</div>
            {items.map((item) => (
              <div key={item.path} className="nav-item">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? "active" : ""}`
                  }
                >
                  <i className={item.icon}></i>
                  <span className="nav-link-text">{item.label}</span>
                  {item.badge && (
                    <span className="nav-badge">{item.badge}</span>
                  )}
                </NavLink>
              </div>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer mt-auto p-3">
        <div className="text-center">
          <small className="text-muted">Version 1.0.0</small>
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
